import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { RouterOutlet } from '@angular/router';
import {
  ResourceManagerService,
  ResourceSet,
} from '@barthauer/abc/core/resources';
import { AutoCompletePanelBehaviour } from '@barthauer/abc/ui-components';
import {
  BasysMapFrameService,
  ViewStateChangeArgType,
} from '@barthauer/basys/map-frame';
import { Subscription } from 'rxjs';
import { ticketsAnimation } from '../../animations';
import {
  ComponentDataSource,
  DataSourceFilter,
  FilterOperator,
} from '../../models/componentDataSource.model';
import { AppService } from '../../services/app.service';
import { FrameService } from '../../services/frame.service';
import { ViewModelHelper } from '../../shared/helpers';
import {
  IMobileTicket,
  MobileTicketViewModel,
} from '../../viewModels/mobileData.tickets.viewModel';
import {
  TicketNavigationContext,
  TicketsDataContext,
  TicketViewPackage,
} from '../tickets.model';
import { TicketsService } from '../tickets.service';

@Component({
  selector: 'app-tickets',
  templateUrl: './tickets.component.html',
  styleUrls: ['./tickets.component.scss'],
  animations: [ticketsAnimation],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class TicketsComponent implements OnInit, OnDestroy, AfterViewInit {
  public res = ResourceSet.Empty;
  AppService = AppService;
  AutoCompletePanelBehaviour = AutoCompletePanelBehaviour;
  dataSource: ComponentDataSource<IMobileTicket>;
  allAutocompleteItems: string[];
  console = console;
  ViewModelHelper = ViewModelHelper;
  private dataContext: TicketsDataContext;
  private subscriptions: Subscription[] = [];
  private isFirstNavigation: boolean = true;

  @ViewChild('searchMenuTrigger', { static: false })
  searchMenuTrigger: MatMenuTrigger;

  @ViewChild('routerOutlet') routerOutlet!: RouterOutlet;
  animationState = 'TicketList';

  constructor(
    public frame: BasysMapFrameService,
    private frameService: FrameService,
    public ticketsService: TicketsService,
    private resourceService: ResourceManagerService,
    private cdr: ChangeDetectorRef,
  ) {
    this.dataSource = new ComponentDataSource();
    this.dataContext = new TicketsDataContext(ticketsService);

    this.subscriptions.push(
      this.frameService.onMobileTicketsSent().subscribe(async (tvp) => {
        if (this.dataContext.dataSource) {
          this.dataContext.resetFilter();
        }
        const ticketCount = tvp.allTicketsFilteredByEntities?.length;

        if (ticketCount === 1) {
          if (this.isFirstNavigation) {
            setTimeout(async () => {
              await this.navigateToTicketDetail();
            }, 0);
            this.isFirstNavigation = false;
          } else {
            await this.navigateToTicketDetail();
          }
        } else if (ticketCount > 1) {
          this.isFirstNavigation = false;
          this.frame.routing.navigateName(AppService.Route_TicketList);
        }

        const navigateToFirstTicket: boolean =
          tvp.filterEntities && tvp.filterEntities.length === 1;
        await this.setDataSource(tvp);

        setTimeout(() => {
          if (navigateToFirstTicket) {
            if (tvp.filterEntities && tvp.filterEntities.length > 0) {
              this.dataContext.resetFilter();
              for (const filterEntity of tvp.filterEntities) {
                const dsFilter: DataSourceFilter = new DataSourceFilter();
                dsFilter.objectInfos = [filterEntity];
                dsFilter.operator = FilterOperator.Or;
                this.dataContext.applyFilterObject(dsFilter);
              }
            }
          }
        }, 0);
      }),
    );

    this.subscriptions.push(
      frame.lib.onViewStateChanged().subscribe((args) => {
        switch (args.type) {
          case ViewStateChangeArgType.BottomSheetPanelOpened: {
            this.ticketsService.sendDataContext(this.dataContext);
            this.cdr.detectChanges();
            break;
          }
        }
      }),
    );
  }

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet(
      'abc-maintenance-opera',
    );
  }

  private async navigateToTicketDetail(): Promise<void> {
    const succ = await this.frame.routing.navigateName(
      AppService.Route_TicketDetail,
    );
    if (succ) {
      const context: TicketNavigationContext = new TicketNavigationContext();
      context.ticketDataContext = this.dataContext;
      context.ticketIndex = 0;
      this.ticketsService.refreshNavigationContext(context);
    }
  }

  private async setDataSource(pack: TicketViewPackage): Promise<void> {
    this.dataContext.statusListDataSource = pack.statusListDataSource;
    if (this.dataSource.rawData !== pack.allTickets) {
      this.dataSource.rawData = pack.allTickets;
      this.dataContext.dataSource = this.dataSource;
      this.dataContext.dataSource.externalFilterCondition = (obj) => {
        const ticket: MobileTicketViewModel = <MobileTicketViewModel>obj;
        return ticket?.ticketTasks?.length > 0;
      };
    }

    if (pack.allTickets) {
      this.allAutocompleteItems = ViewModelHelper.flattenAndDistinctToArray(
        pack.allTickets,
      ).sort();
    }
  }

  public applyFilter(filter: string): void {
    setTimeout(() => this.searchMenuTrigger.closeMenu(), 0);
    const dsFilter: DataSourceFilter = new DataSourceFilter();
    dsFilter.value = filter;
    this.dataContext.applyFilterObject(dsFilter);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
    this.frameService.dispose();
  }

  ngAfterViewInit() {
    this.animationState = ViewModelHelper.getAnimationData(this.routerOutlet);
    this.cdr.detectChanges();
  }
}
